// @flow
import {
  AWS,
  DEDICATED_CLUSTER_TYPE,
  ENTERPRISE_CLUSTER_TYPE,
  PEERED_VPC,
  RATE_CARD_VERSION_4_11_2024,
  NAN,
  RATE_CARD_VERSION_8_14_2025,
  RATE_CARD_VERSION_5_15_2026,
} from '@src/constants';
import {
  getLinkForOrganizationsPage,
  getLinkForSPDefaultCommitDetailsPage,
  getLinkForStreamingProjectsHomePage,
  getLinkForStreamingProjectsSPPage,
  getLinkForSPUserCommitDetailsPage,
} from '@streaming-projects/links';
import { StyledSpan } from '@src/common-utils/styledComponents';
import { Link } from 'react-router-dom';
import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { formatFloat4DP } from '@components/views/grid-utils/renderers';
import { isFormEditedBasedOnCurrentValues } from '@src/common-utils/utils';
import { number } from 'yup';

import { BACKEND_FIELDNAMES } from './enums';
import { CLUSTER_RESOURCES_GRID_FIELDNAMES } from './orgs/enums';

export const getRCVersionOptionsFromRCs = (rcVersions, isRenewal) => {
  const rcVersionsOptions = [];
  for (const rc of rcVersions) {
    if (
      isRenewal &&
      (rc.name === RATE_CARD_VERSION_8_14_2025 || rc.name === RATE_CARD_VERSION_5_15_2026)
    ) {
      continue;
    }

    const actionable = rc?.actionable ?? true;
    rcVersionsOptions.push({
      name: rc.name,
      display_name: rc.label,
      shouldNotBeSelectable: !actionable,
    });
  }
  return [...rcVersionsOptions].reverse();
};

export const getOrgDefaultValues = (account) => {
  return {
    [BACKEND_FIELDNAMES.ACCOUNT_ID]: account[BACKEND_FIELDNAMES.ID],
    [BACKEND_FIELDNAMES.ACCOUNT_NAME]: account[BACKEND_FIELDNAMES.NAME],
  };
};

export const getSPOrgBreadcrumbs = (spOrgData) => {
  const orgId = spOrgData?.org?.id;
  const orgName = spOrgData?.org?.sfdc_org_name;
  return [
    <a href={getLinkForStreamingProjectsHomePage()} key="home">
      Home
    </a>,
    <StyledSpan key="sp-org"> Organization </StyledSpan>,
    <Link key="sp-org-name" to={getLinkForOrganizationsPage(orgId)}>
      {orgName}
    </Link>,
  ];
};

export const getSPStreamingProjectBreadcrumbs = (spOrgData, spStreamingProjectData) => {
  const orgId = spOrgData?.org?.id;
  return [
    ...getSPOrgBreadcrumbs(spOrgData),
    <StyledSpan key="sp-sp"> Streaming Project </StyledSpan>,
    <Link
      key="sp-sp-name"
      to={getLinkForStreamingProjectsSPPage(orgId, spStreamingProjectData?.sp?.sp_id)}
    >
      {spStreamingProjectData?.sp?.name}
    </Link>,
  ];
};

export const getSPForecastStatusPageBreadcrumbs = (spOrgData) => {
  return [
    ...getSPOrgBreadcrumbs(spOrgData),
    <StyledSpan key="sp-forecast-page"> Streaming Projects Status Dashboard </StyledSpan>,
  ];
};

export const getWorkloadBreadcrumbs = (
  spOrgData,
  spStreamingProjectData,
  workLoadName,
  workloadType
) => {
  return [
    ...getSPStreamingProjectBreadcrumbs(spOrgData, spStreamingProjectData),
    <StyledSpan key="sp-workload"> Workload </StyledSpan>,
    <StyledSpan key="sp-workload-type"> {workloadType} </StyledSpan>,
    workLoadName,
  ];
};

export const getSPDefaultCommitDetailsBreadcrumbs = (spOrgData) => {
  const orgId = spOrgData?.org?.id;

  if (orgId) {
    return [
      ...getSPOrgBreadcrumbs(spOrgData),
      <StyledSpan key="sp-commit"> Commit </StyledSpan>,
      <Link key="org-default-commit-name" to={getLinkForSPDefaultCommitDetailsPage(orgId)}>
        Default Commit
      </Link>,
    ];
  }
};

export const getSPUserCommitDetailsBreadcrumbs = (spOrgData, spUserCommitData) => {
  const orgId = spOrgData?.org?.id;

  // CTODO::SP check if commit_id is the correct field name
  const commitId = spUserCommitData?.commit_details?.id;

  if (orgId) {
    return [
      ...getSPOrgBreadcrumbs(spOrgData),
      <StyledSpan key="sp-commit"> Commit </StyledSpan>,
      <Link key="user-commit-name" to={getLinkForSPUserCommitDetailsPage(orgId, commitId)}>
        {/* CTODO::SP check Commit Name */}
        {spUserCommitData?.commit_details?.name}
      </Link>,
    ];
  }
};

export const getProviderRegionsMapFromMetaData = (apiData) => {
  const providerRegionsMap = {};
  const providerNameLabelMap = {};
  apiData.resource_region_details.region_details.forEach(
    ({ provider_label: providerLabel, provider_name: providerName, region }) => {
      providerRegionsMap[providerName] = {
        regions: region.reduce((acc, region) => {
          acc[region.region_name] = region.region_label;
          return acc;
        }, {}),
        providerLabel,
      };
      providerNameLabelMap[providerName] = providerLabel;
    }
  );

  return { providerRegionsMap, providerNameLabelMap };
};

export const shouldShowFFOption = (values, isUserAdmin) => {
  return (
    values[CLUSTER_RESOURCES_GRID_FIELDNAMES.PROVIDER] === AWS &&
    values[CLUSTER_RESOURCES_GRID_FIELDNAMES.CLUSTER_TYPE] === DEDICATED_CLUSTER_TYPE &&
    values[CLUSTER_RESOURCES_GRID_FIELDNAMES.NETWORKING_TYPE] === PEERED_VPC &&
    isUserAdmin
  );
};

export const shouldShowDedicatedPerCKUPartitionLimitIncreaseToggleOption = (values) => {
  return values[CLUSTER_RESOURCES_GRID_FIELDNAMES.CLUSTER_TYPE] === DEDICATED_CLUSTER_TYPE;
};

export const isExpandedInputNeeded = (clusterType, rateCardVersion) => {
  if (
    rateCardVersion === RATE_CARD_VERSION_4_11_2024 ||
    rateCardVersion === RATE_CARD_VERSION_8_14_2025 ||
    rateCardVersion === RATE_CARD_VERSION_5_15_2026
  ) {
    return true;
  }
  const CLUSTER_TYPES_FOR_WHICH_EXPANDED_INPUTS_ARE_NEEDED_FOR_ALL_RCS = [
    DEDICATED_CLUSTER_TYPE,
    ENTERPRISE_CLUSTER_TYPE,
  ];

  return CLUSTER_TYPES_FOR_WHICH_EXPANDED_INPUTS_ARE_NEEDED_FOR_ALL_RCS.includes(clusterType);
};

export const getMonthsAndYearsTransformedMonthlySpendSummary = (monthlySpendSummary) => {
  const clonedMonthlySpendSummary = cloneDeep(monthlySpendSummary ?? []);
  // todo::SP Talk to BE and remove the need for the transformation to be done here
  clonedMonthlySpendSummary.forEach((item) => {
    item.yearlies.forEach((yearly) => {
      // Update calendar_year to year
      yearly.year = yearly.calendar_year;
      // Update calendar_month to month for each month
      yearly.months?.forEach((month) => {
        month.month = month.calendar_month;
      });
    });
  });

  return clonedMonthlySpendSummary;
};

export const isGivenDataInOldFormatForMonthlySpendSummary = (arrayOfRowObjects) => {
  if (
    arrayOfRowObjects.length > 0 &&
    Object.prototype.hasOwnProperty.call(arrayOfRowObjects[0], 'cost')
  ) {
    return true;
  }
  return false;
};

export const getValueOrNaNFromDataObject = (data) => {
  return data.is_nan ? NAN : formatFloat4DP(Number(data.value));
};

export const isAnythingEditedBesidesGivenFormBasedOnGivenConfig = (
  initialValues,
  values,
  config,
  currentFormName = null
) => {
  if (!currentFormName) {
    return isFormEditedBasedOnCurrentValues(values, initialValues, config);
  }
  const formFieldsNotInCurrentForm = config.filter(
    (config) => config.belongingForm !== currentFormName
  );

  return isFormEditedBasedOnCurrentValues(values, initialValues, formFieldsNotInCurrentForm);
};

export const infiniteRetentionInputValidationFunc = ({ dealDuration }) => {
  const dealLengthInDays = dealDuration.deal_duration_days;
  return number()
    .min(0)
    .label('Retention (days)')
    .required()
    .max(dealLengthInDays)
    .typeError('You must specify a number');
};

export const getTableFlowStorageTypeMapMapFromMetaData = (apiData) => {
  const tableflowParams = apiData?.tableflow_params;

  if (!tableflowParams) {
    return {};
  }

  return tableflowParams.reduce((acc, curr) => {
    acc[curr.storage_type] = curr.storage_type_label;
    return acc;
  }, {});
};
